import dagre from "@dagrejs/dagre";

// Pure graph auto-layout used by the "Rebuild map" action. Decoupled from
// ReactFlow / the store: it takes plain node boxes + edges and returns new
// top-left positions keyed by node id. Dagre reports node centres, so we
// convert back to the top-left origin ReactFlow expects.

export interface LayoutNode {
  id: string;
  width: number;
  height: number;
}

export interface LayoutEdge {
  source: string;
  target: string;
}

export interface LayoutOptions {
  direction?: "TB" | "BT" | "LR" | "RL"; // rank direction (default top→bottom)
  nodeSep?: number;                       // gap between nodes in the same rank
  rankSep?: number;                       // gap between ranks
}

export function computeAutoLayout(
  nodes: LayoutNode[],
  edges: LayoutEdge[],
  opts: LayoutOptions = {},
): Record<string, { x: number; y: number }> {
  const positions: Record<string, { x: number; y: number }> = {};
  if (nodes.length === 0) return positions;

  const g = new dagre.graphlib.Graph();
  g.setGraph({
    rankdir: opts.direction ?? "TB",
    nodesep: opts.nodeSep ?? 60,
    ranksep: opts.rankSep ?? 90,
    marginx: 20,
    marginy: 20,
  });
  g.setDefaultEdgeLabel(() => ({}));

  const ids = new Set(nodes.map((n) => n.id));
  nodes.forEach((n) => g.setNode(n.id, { width: n.width, height: n.height }));
  // Skip edges that reference a node not in the set (e.g. shapes) — dagre would
  // otherwise auto-create a zero-size node for the missing endpoint.
  edges.forEach((e) => {
    if (ids.has(e.source) && ids.has(e.target)) g.setEdge(e.source, e.target);
  });

  dagre.layout(g);

  nodes.forEach((n) => {
    const p = g.node(n.id);
    if (p) positions[n.id] = { x: p.x - n.width / 2, y: p.y - n.height / 2 };
  });
  return positions;
}
